import get from 'lodash/get';
import { mutationTypes, factoryOptionsUtils } from '@helpers/storeFactories/helpers';
import selectItemActions from './actions/selectItemActions';
import selectItemMutations from './mutations/selectItemMutations';
import selectItemGetters from './getters/selectItemGetters';
import { storeConstants } from '@/utils/constants';
import { storeUtils } from '@/utils';
import { LogService } from '@/services';
import notificationUtils from '@/utils/notificationUtils';

const defaultState = {
    list: [],
    listMap: {},
    listFilters: null,
    listSort: null,
    totalCount: 0,
    timestamp: 0,
    loadsInProgress: 0,
    allItemsLoaded: false,
    refresh: false,
    totals: null,
};
/**
 *
 * @param {*} storeName
 * @param {*} api
 * @param {*} options
 * @param {*} options.normalizeItemFn normalization function for each item in the list
 * @param {*} options.statePath full path for store on state ( needed for submodules ex: 'cities.cityWithState')
 * @param {*} options.actions { create|update|delete|selected } - add actions for store ( { create: true } - will add create )
 * @param {*} options.actionOptions - action options
 * @param {*} options.actionOptions.get - option
 * @param {*} options.actionOptions.get.controlItems ALL|CUSTOM - control how to get the items ( all or outside control)
 * @param {*} options.actionOptions.get.defaultOption { refresh, offset, limit } - if no option is sent on the get ( only for custom ) this will be used
 * @returns
 */

function _createStore(storeName, api, options) {
    const actionGetOptionsDefault = factoryOptionsUtils.getActionGetDefaultOptions(options);
    const actionGetControlItems = factoryOptionsUtils.getActionControlItem(options);
    const normalizeItemFn = factoryOptionsUtils.getNormalizeItemFn(options);

    if (!api.get) {
        throw new Error('Please provide the endpoint get for ', storeName);
    }

    const store = {
        name: storeName,
        namespaced: true,
        state: { ...defaultState },
        getters: {
            isLoading: (state) => !!state.loadsInProgress,
            filterByCol: (state) => (colName, value) =>
                state.list.filter((item) => get(item, colName) === value),
            getFirstByCol: (state) => (colName, value) =>
                state.list.find((item) => get(item, colName) === value),
            getById: (state) => (id) => state.listMap[id],
            ...(factoryOptionsUtils.hasActionSelect(options) ? selectItemGetters : {}),
        },
        mutations: {
            [mutationTypes.SET_LIST](state, list) {
                state.timestamp = new Date().getTime();
                list = normalizeItemFn ? [...list].map(normalizeItemFn) : [...list];
                state.list = list;
                state.listMap = list.reduce((list, item) => {
                    list[item.id] = item;

                    return list;
                }, {});
            },
            [mutationTypes.SET_TOTALS](state, totals) {
                state.totals = totals;
            },
            [mutationTypes.SET_LIST_FILTER](state, listFilters) {
                state.listFilters = listFilters ? { ...listFilters } : null;
            },
            [mutationTypes.SET_LIST_SORT](state, listSort) {
                state.listSort = [...listSort];
            },
            [mutationTypes.APPEND_TO_LIST](state, list) {
                state.timestamp = new Date().getTime();
                list = normalizeItemFn ? [...list].map(normalizeItemFn) : [...list];
                state.list = [...state.list, ...list];
                state.listMap = {
                    ...state.listMap,
                    ...list.reduce((list, item) => {
                        list[item.id] = item;

                        return list;
                    }, {}),
                };
            },
            [mutationTypes.CLEAR_LIST](state) {
                state.list = [];
            },
            [mutationTypes.CLEAR_TOTALS](state) {
                state.totals = [];
            },
            [mutationTypes.CLEAR_LIST_FILTER](state) {
                state.listFilter = [];
            },
            [mutationTypes.CLEAR_LIST_SORT](state) {
                state.listSort = [];
            },
            [mutationTypes.CLEAR_DATA](state) {
                state.list = [];
                state.listFilters = null;
                state.listSort = null;
                state.loading = 0;
                state.loadsInProgress = 0;
                state.allItemsLoaded = false;
                state.refresh = false;
                state.totals = [];
                state.timestamp = null;
            },
            [mutationTypes.INCREMENT_LOADINGS_IN_PROGRESS](state) {
                state.loadsInProgress = state.loadsInProgress + 1;
            },
            [mutationTypes.DECREMENT_LOADINGS_IN_PROGRESS](state) {
                state.loadsInProgress = state.loadsInProgress - 1;
                if (state.loadsInProgress < 0) {
                    LogService.warn('loads in progress is not ok');
                }
            },
            [mutationTypes.SET_REFRESH](state, refresh) {
                state.refresh = refresh;
            },
            [mutationTypes.CREATE_RAW_ITEM](state, { id, data }) {
                state.list = [data, ...state.item];
                state.listMap[id] = data;
            },
            [mutationTypes.CREATE_ITEM](state, { id, data }) {
                const item = normalizeItemFn ? normalizeItemFn(data) : data;
                state.list = [...state.list, item];
                state.listMap[id] = item;
            },
            [mutationTypes.UPDATE_ITEM](state, { id, data }) {
                if (!state.listMap[id]) return false;

                const item = normalizeItemFn ? normalizeItemFn(data) : data;
                const index = state.list.findIndex((item) => item.id === id);
                Object.assign(state.list[index], item);
                Object.assign(state.listMap[id], item);
            },
            [mutationTypes.CREATE_OR_UPDATE_ITEM](state, { id, data }) {
                const item = normalizeItemFn ? normalizeItemFn(data) : data;

                if (state.listMap[id]) {
                    const index = state.list.findIndex((item) => item.id === id);
                    Object.assign(state.list[index], item);
                    Object.assign(state.listMap[id], item);
                } else {
                    state.list = [item, ...state.list];

                    state.listMap[id] = item;
                }
            },
            [mutationTypes.DELETE_ITEM](state, id) {
                if (!state.listMap[id]) return false;

                const index = state.list.findIndex((item) => item.id === id);
                state.list.splice(index, 1);
                delete state.listMap[id];
            },
            [mutationTypes.SORT](state, { col, direction, fn, getSortValueFn, reverse }) {
                let getDirection = () => {
                        if (
                            direction === storeConstants.SORT_DIRECTION_ASCENDING ||
                            direction === storeConstants.SORT_DIRECTION_DESCENDING
                        ) {
                            return direction;
                        }
                        if (reverse && col === state.sort.col) {
                            return state.sort.direction === storeConstants.SORT_DIRECTION_ASCENDING
                                ? storeConstants.SORT_DIRECTION_DESCENDING
                                : storeConstants.SORT_DIRECTION_ASCENDING;
                        }
                        return storeConstants.SORT_DIRECTION_ASCENDING;
                    },
                    applyNewSortOnState = () => {
                        state.sort = {
                            col: col || state.sort.col || state.defaultSortCol,
                            direction: getDirection(),
                            fn: fn || null,
                            getSortValueFn: getSortValueFn || null,
                        };
                    },
                    applyCustomSortFn = () => {
                        return state.collection.sort(state.sort.fn);
                    },
                    applyDefaultSortFn = () => {
                        return state.collection.sort((a, b) => {
                            let valueA = state.sort.getSortValueFn
                                    ? state.sort.getSortValueFn({ row: a })
                                    : a[state.sort.col],
                                valueB = state.sort.getSortValueFn
                                    ? state.sort.getSortValueFn({ row: b })
                                    : b[state.sort.col];

                            if (typeof valueA === 'string') valueA = valueA.toLowerCase();
                            if (typeof valueB === 'string') valueB = valueB.toLowerCase();

                            if (valueA < valueB) {
                                return state.sort.direction ===
                                    storeConstants.SORT_DIRECTION_DESCENDING
                                    ? 1
                                    : -1;
                            }
                            if (valueA > valueB) {
                                return state.sort.direction ===
                                    storeConstants.SORT_DIRECTION_DESCENDING
                                    ? -1
                                    : 1;
                            }
                            return 0;
                        });
                    };

                applyNewSortOnState();
                if (state.sort.fn) return applyCustomSortFn();
                return applyDefaultSortFn();
            },
            ...(factoryOptionsUtils.hasActionSelect(options) ? selectItemMutations : {}),
        },
        actions: {
            async one({ state, commit, getters, dispatch }, { id, refresh, silent }) {
                const needsRefresh =
                    refresh ||
                    state.refresh ||
                    storeUtils.isStoreOutDated(state.timestamp, storeConstants.REFRESH_TIME);

                if (needsRefresh || !getters.getById(id)) {
                    const response = await api.one(id);
                    if (response.success) {
                        commit(mutationTypes.CREATE_OR_UPDATE_ITEM, { id, data: response.data });
                    } else {
                        silent &&
                            dispatch(
                                'notification/error',
                                notificationUtils.getFromApiResponse(response),
                                { root: true }
                            );
                        commit(mutationTypes.DELETE_ITEM, id);
                    }
                }

                return getters.getById(id);
            },
            async search(store, options = actionGetOptionsDefault) {
                const response = await api.get({
                    offset: options.offset,
                    limit: options.limit,
                    filter: options.filter,
                    sort: options.sort,
                });
                if (response.success) {
                    return {
                        ...response,
                        list: normalizeItemFn
                            ? response.data.list.map(normalizeItemFn)
                            : response.data.list,
                    };
                }
                return response;
            },
            sort({ commit }, { col, direction, fn, getSortValueFn, reverse }) {
                commit(mutationTypes.SORT, { col, direction, fn, getSortValueFn, reverse });
            },
            setRefreshIfOutdated({ commit, state }, timestamp) {
                if (timestamp > state.timestamp) {
                    commit(mutationTypes.SET_REFRESH, true);
                }
            },
            setRefresh({ commit }) {
                commit(mutationTypes.SET_REFRESH, true);
            },
            ...(factoryOptionsUtils.hasActionSelect(options) ? selectItemActions : {}),
            async create({ commit, state, dispatch }, { data, silent }) {
                const response = await api.create(data);

                if (!response.success) {
                    silent &&
                        dispatch(
                            'notification/error',
                            notificationUtils.getFromApiResponse(response),
                            { root: true }
                        );

                    return response;
                } else {
                    const responseData = response.data;

                    commit(mutationTypes.CREATE_ITEM, { id: responseData.id, data: responseData });

                    return {
                        success: true,
                        data: state.listMap[responseData.id],
                    };
                }
            },
            async update({ commit, state, dispatch }, { id, data, silent }) {
                const response = await api.update(id, data);

                if (!response.success) {
                    silent &&
                        dispatch(
                            'notification/error',
                            notificationUtils.getFromApiResponse(response),
                            { root: true }
                        );

                    return response;
                } else {
                    const responseData = response.data;

                    commit(mutationTypes.CREATE_OR_UPDATE_ITEM, {
                        id: responseData.id,
                        data: responseData,
                    });

                    return {
                        success: true,
                        data: state.listMap[responseData.id],
                    };
                }
            },
            async delete({ commit, dispatch, getters }, { id, silent }) {
                if (getters.getById(id)) {
                    const response = await api.delete(id);

                    if (!response.success) {
                        silent &&
                            dispatch(
                                'notification/error',
                                notificationUtils.getFromApiResponse(response),
                                { root: true }
                            );
                        return response;
                    } else {
                        commit(mutationTypes.DELETE_ITEM, id);
                        return { success: true };
                    }
                } else {
                    return { success: false };
                }
            },
            async instantDelete({ commit, dispatch, getters }, { id, silent }) {
                const item = [...getters.getById(id)];
                if (item) {
                    commit(mutationTypes.DELETE_ITEM, id);
                    const response = await api.delete(id);

                    if (!response.success) {
                        silent &&
                            dispatch(
                                'notification/error',
                                notificationUtils.getFromApiResponse(response),
                                { root: true }
                            );
                        commit(mutationTypes.CREATE_RAW_ITEM, { id: item.id, data: item });

                        return response;
                    } else {
                        return { success: true };
                    }
                } else {
                    return { success: false };
                }
            },
        },
    };

    if (!factoryOptionsUtils.hasActionCreate(options)) {
        delete store.actions.create;
    }
    if (!factoryOptionsUtils.hasActionUpdate(options)) {
        delete store.actions.update;
    }
    if (!factoryOptionsUtils.hasActionDelete(options)) {
        delete store.actions.delete;
        delete store.actions.instantDelete;
    }

    if (actionGetControlItems === storeConstants.ACTION_GET_CONTROL_ITEMS.CUSTOM) {
        store.actions.get = async ({ state, commit }, options = actionGetOptionsDefault) => {
            const needsRefresh =
                options.refresh ||
                state.refresh ||
                storeUtils.isStoreOutDated(state.timestamp, storeConstants.REFRESH_TIME);

            if (needsRefresh) {
                commit(mutationTypes.CLEAR_LIST);
                commit(mutationTypes.SET_ALL_ITEMS_LOADED, false);
            }
            commit(mutationTypes.INCREMENT_LOADINGS_IN_PROGRESS);
            const response = await api.get({
                offset: options.offset,
                limit: options.limit,
                filter: options.filter,
                sort: options.sort,
            });
            if (response.success) {
                const { list, totals } = response.data;

                commit(mutationTypes.SET_TOTALS, totals);
                commit(mutationTypes.SET_REFRESH, false);
                commit(mutationTypes.APPEND_TO_LIST, list);
                commit(mutationTypes.DECREMENT_LOADINGS_IN_PROGRESS);
                return state.list;
            } else {
                LogService.debug(`problem loading get custom ${storeName}`);
                commit(mutationTypes.DECREMENT_LOADINGS_IN_PROGRESS);
                return null;
            }
        };
    }

    if (actionGetControlItems === storeConstants.ACTION_GET_CONTROL_ITEMS.ALL) {
        const factoryOptions = options;
        store.actions.get = async function (
            { state, commit, getters, dispatch },
            options = actionGetOptionsDefault
        ) {
            const needsRefresh =
                (options && options.refresh) ||
                state.refresh ||
                storeUtils.isStoreOutDated(state.timestamp, storeConstants.REFRESH_TIME);

            if (getters.isLoading) {
                let promiseLoading = new Promise((resolve) => {
                    let unwatchLoading = this.watch(
                        (state) => {
                            return get(state, factoryOptions.statePath || storeName)
                                .loadsInProgress;
                        },
                        (val) => {
                            if (!val) {
                                unwatchLoading();
                                resolve(state.list);
                            }
                        }
                    );
                });
                await promiseLoading;
            } else {
                if (needsRefresh) {
                    commit(mutationTypes.CLEAR_DATA);
                    commit(mutationTypes.INCREMENT_LOADINGS_IN_PROGRESS);

                    let finalList = [];
                    let finalTotals = [];
                    let offset = actionGetOptionsDefault.offset;
                    let finish = false;
                    let lastResponse = {};
                    const buildStateFromResponse = function (response) {
                        if (response.success) {
                            let { list, totals } = response.data;
                            totals = Array.isArray(totals) ? totals : [];

                            finalList = [...finalList, ...list];
                            finalTotals = finalTotals && finalTotals.length ? finalTotals : totals;
                            finish = list.length < actionGetOptionsDefault.limit;
                            offset += actionGetOptionsDefault.limit;
                        } else {
                            dispatch(
                                'notification/error',
                                notificationUtils.getFromApiResponse(response),
                                { root: true }
                            );
                            finish = true;
                        }
                    };

                    while (!finish) {
                        lastResponse = await api.get({
                            filter: options.filter,
                            sort: options.sort,
                            limit: actionGetOptionsDefault.limit,
                            ...options,
                            offset,
                        });

                        buildStateFromResponse(lastResponse);
                    }

                    commit(mutationTypes.SET_LIST_FILTER, options.filter);
                    commit(mutationTypes.SET_LIST, finalList);
                    commit(mutationTypes.SET_TOTALS, finalTotals);
                    commit(mutationTypes.DECREMENT_LOADINGS_IN_PROGRESS);
                }
            }

            return state.list;
        };
        store.actions.reload = async function ({ state, dispatch }) {
            await dispatch('get', { refresh: true, filter: state.listFilters });
        };
    }

    return store;
}

function createStore(storeName, api, options = {}, overrides = {}) {
    let store = _createStore(storeName, api, options);

    !overrides.getters || Object.assign(store.getters, overrides.getters);
    !overrides.mutations || Object.assign(store.mutations, overrides.mutations);
    !overrides.actions || Object.assign(store.actions, overrides.actions);
    !overrides.state || Object.assign(store.state, overrides.state);
    !overrides.modules || (store.modules = overrides.modules);

    return store;
}

export default {
    createStore,
};
